import type { AxialCoord, HexTile, GridConfig } from '@/types/hex.types';
import { PlayerSide } from '@/types/hex.types';
import { coordToKey, keyToCoord, getNeighbors } from './HexCoord';
import { DEFAULT_GRID_CONFIG, PLAYER_ROWS } from '@/utils/constants';

/**
 * Hex grid data structure
 * Uses offset coordinates for rectangular grid layout
 */
export class HexGrid {
  private tiles: Map<string, HexTile> = new Map();
  private config: GridConfig;

  constructor(config: GridConfig = DEFAULT_GRID_CONFIG) {
    this.config = config;
    this.initializeGrid();
  }

  /**
   * Initialize the hex grid with tiles
   */
  private initializeGrid(): void {
    const { width, height } = this.config;

    for (let row = 0; row < height; row++) {
      for (let col = 0; col < width; col++) {
        // Convert offset coordinates to axial
        const coord = this.offsetToAxial(col, row);
        const owner = this.determineOwner(row);

        const tile: HexTile = {
          coord,
          owner,
          occupant: null,
        };

        this.tiles.set(coordToKey(coord), tile);
      }
    }
  }

  /**
   * Convert offset (col, row) to axial coordinates
   * Uses odd-r offset for pointy-top hexes
   */
  private offsetToAxial(col: number, row: number): AxialCoord {
    const q = col - Math.floor(row / 2);
    const r = row;
    return { q, r };
  }

  /**
   * Convert axial to offset coordinates
   */
  public axialToOffset(coord: AxialCoord): { col: number; row: number } {
    const col = coord.q + Math.floor(coord.r / 2);
    const row = coord.r;
    return { col, row };
  }

  /**
   * Determine which player owns a row
   */
  private determineOwner(row: number): PlayerSide | null {
    const { height } = this.config;

    // Bottom rows belong to Player 1
    if (row >= height - PLAYER_ROWS) {
      return PlayerSide.Player1;
    }
    // Top rows belong to Player 2
    if (row < PLAYER_ROWS) {
      return PlayerSide.Player2;
    }
    // Middle rows are neutral (if any)
    return null;
  }

  /**
   * Get a tile by axial coordinates
   */
  getTile(coord: AxialCoord): HexTile | undefined {
    return this.tiles.get(coordToKey(coord));
  }

  /**
   * Get a tile by offset coordinates
   */
  getTileByOffset(col: number, row: number): HexTile | undefined {
    const coord = this.offsetToAxial(col, row);
    return this.getTile(coord);
  }

  /**
   * Check if a coordinate is within the grid
   */
  isValidCoord(coord: AxialCoord): boolean {
    return this.tiles.has(coordToKey(coord));
  }

  /**
   * Get all tiles in the grid
   */
  getAllTiles(): HexTile[] {
    return Array.from(this.tiles.values());
  }

  /**
   * Get tiles owned by a specific player
   */
  getPlayerTiles(player: PlayerSide): HexTile[] {
    return this.getAllTiles().filter((tile) => tile.owner === player);
  }

  /**
   * Get valid neighbors of a tile
   */
  getValidNeighbors(coord: AxialCoord): HexTile[] {
    return getNeighbors(coord)
      .filter((c) => this.isValidCoord(c))
      .map((c) => this.getTile(c)!)
      .filter((tile) => tile !== undefined);
  }

  /**
   * Set occupant on a tile
   */
  setOccupant(coord: AxialCoord, entityId: string | null): boolean {
    const tile = this.getTile(coord);
    if (!tile) return false;

    tile.occupant = entityId;
    return true;
  }

  /**
   * Check if a tile is occupied
   */
  isOccupied(coord: AxialCoord): boolean {
    const tile = this.getTile(coord);
    return tile?.occupant !== null;
  }

  /**
   * Get grid configuration
   */
  getConfig(): Readonly<GridConfig> {
    return this.config;
  }

  /**
   * Get all tile coordinates
   */
  getAllCoords(): AxialCoord[] {
    return Array.from(this.tiles.keys()).map(keyToCoord);
  }

  /**
   * Find empty tiles for a player
   */
  getEmptyPlayerTiles(player: PlayerSide): HexTile[] {
    return this.getPlayerTiles(player).filter((tile) => tile.occupant === null);
  }
}
